import { useEffect, useRef, useState } from "react";

import {
  aeroWorkbenchApi,
  type AeroLiftingLineCondition,
  type AeroThreeDComparisonContent,
  type AeroThreeDReferenceKind,
  type AeroThreeDSpanwiseBasis,
  type AeroThreeDSpanwiseReferencePoint,
} from "../../api/aeroWorkbench";

type CompareState = "idle" | "saving" | "comparing" | "done" | "warning" | "error";

const matchingSpanwiseBasis = {
  coordinate: "body_y",
  load_density: "per_body_y",
  lift_axis: "projected_body_plus_z_normal_to_freestream",
  drag_axis: "positive_freestream",
} as const satisfies AeroThreeDSpanwiseBasis;

function optionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const number = Number(value);
  if (!Number.isFinite(number)) throw new Error(`Expected a finite number, received ${value}`);
  return number;
}

export function parseSpanwise(value: string, defaultSurfaceId: string): AeroThreeDSpanwiseReferencePoint[] | undefined {
  const lines = value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return undefined;
  const points: AeroThreeDSpanwiseReferencePoint[] = [];
  for (const [index, line] of lines.entries()) {
    const columns = line.split(/[\s,;]+/).filter(Boolean);
    if (
      index === 0
      && /^(surface|surface_id)$/i.test(columns[0] ?? "")
      && /^(y|y_m|body_y|body_y_m|span|span_m|span_coordinate_m)$/i.test(columns[1] ?? "")
    ) continue;
    const hasSurface = columns.length >= 3 && Number.isNaN(Number(columns[0]));
    const surfaceId = hasSurface ? columns[0]! : defaultSurfaceId;
    const offset = hasSurface ? 1 : 0;
    if (columns.length - offset < 2) {
      throw new Error(`Spanwise line ${index + 1} needs y and lift per span`);
    }
    const span = Number(columns[offset]);
    const lift = Number(columns[offset + 1]);
    const drag = columns[offset + 2] == null ? undefined : Number(columns[offset + 2]);
    if (![span, lift, ...(drag == null ? [] : [drag])].every(Number.isFinite)) {
      throw new Error(`Spanwise line ${index + 1} contains a non-numeric load`);
    }
    points.push({ surface_id: surfaceId, span_coordinate_m: span, lift_n_per_m: lift, ...(drag == null ? {} : { drag_n_per_m: drag }) });
  }
  if (points.length < 2) throw new Error("A spanwise comparison needs at least two real 3D points");
  return points;
}

function newUuid(): string {
  return globalThis.crypto.randomUUID();
}

function formatResidual(value: number | null, digits = 3): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString(undefined, { maximumSignificantDigits: digits });
}

function ThreeDSpanwiseOverlay({ comparison }: { comparison: AeroThreeDComparisonContent }) {
  const points = comparison.spanwise.aligned_points;
  if (!comparison.spanwise.available || !points.length) return null;
  const minX = Math.min(...points.map((point) => point.span_coordinate_m));
  const maxX = Math.max(...points.map((point) => point.span_coordinate_m));
  const values = points.flatMap((point) => [point.lift.prediction, point.lift.reference]);
  const minY = Math.min(0, ...values);
  const maxY = Math.max(0, ...values);
  const width = 640;
  const height = 220;
  const margin = { top: 18, right: 20, bottom: 38, left: 58 };
  const x = (value: number) => margin.left + (value - minX) / Math.max(1e-9, maxX - minX) * (width - margin.left - margin.right);
  const y = (value: number) => margin.top + (maxY - value) / Math.max(1e-9, maxY - minY) * (height - margin.top - margin.bottom);
  const bySurface = new Map<string, typeof points>();
  for (const point of points) bySurface.set(point.surface_id, [...(bySurface.get(point.surface_id) ?? []), point]);
  const line = (surfacePoints: typeof points, quantity: "prediction" | "reference") => [...surfacePoints]
    .sort((left, right) => left.span_coordinate_m - right.span_coordinate_m)
    .map((point, index) => `${index ? "L" : "M"}${x(point.span_coordinate_m).toFixed(2)} ${y(point.lift[quantity]).toFixed(2)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Reduced-order and imported real 3D spanwise lift comparison">
      <rect x={margin.left} y={margin.top} width={width - margin.left - margin.right} height={height - margin.top - margin.bottom} fill="var(--fc-surface-2)" stroke="var(--fc-border)" />
      {[...bySurface.entries()].map(([surfaceId, surfacePoints]) => (
        <g key={surfaceId} data-surface-id={surfaceId}>
          <path d={line(surfacePoints, "reference")} fill="none" stroke="var(--fc-warning)" strokeDasharray="5 4" strokeWidth="2" />
          <path d={line(surfacePoints, "prediction")} fill="none" stroke="var(--fc-primary-bright)" strokeWidth="2" />
        </g>
      ))}
      {points.map((point) => <circle key={`${point.surface_id}:${point.span_coordinate_m}`} cx={x(point.span_coordinate_m)} cy={y(point.lift.reference)} r="2.5" fill="var(--fc-warning)" />)}
      <text x={(margin.left + width - margin.right) / 2} y={height - 7} textAnchor="middle">body span y (m)</text>
      <text x="12" y={(margin.top + height - margin.bottom) / 2} textAnchor="middle" transform={`rotate(-90 12 ${(margin.top + height - margin.bottom) / 2})`}>lift per span (N/m)</text>
    </svg>
  );
}

export function ThreeDComparisonPanel({
  projectId,
  geometryArtifactVersionId,
  predictionArtifactVersionId,
  predictionReferenceAreaM2,
  predictionSurfaceIds,
  predictionCurrent,
  condition,
}: {
  projectId: string | null;
  geometryArtifactVersionId: string | null;
  predictionArtifactVersionId: string | null;
  predictionReferenceAreaM2: number | null;
  predictionSurfaceIds: string[];
  predictionCurrent: boolean;
  condition: AeroLiftingLineCondition;
}) {
  const [sourceKind, setSourceKind] = useState<AeroThreeDReferenceKind>("flow360_rans");
  const [sourceId, setSourceId] = useState("");
  const [runId, setRunId] = useState("");
  const [fidelity, setFidelity] = useState("3D RANS");
  const [referenceSpeedMps, setReferenceSpeedMps] = useState(String(condition.speed_mps));
  const [referenceAlphaDeg, setReferenceAlphaDeg] = useState(String(condition.alpha_deg));
  const [referenceBetaDeg, setReferenceBetaDeg] = useState(String(condition.beta_deg ?? 0));
  const [referenceDensity, setReferenceDensity] = useState(String(condition.density_kg_m3 ?? 1.225));
  const [referenceViscosity, setReferenceViscosity] = useState(String(condition.dynamic_viscosity_pa_s ?? 1.81e-5));
  const [referenceSoundSpeed, setReferenceSoundSpeed] = useState(String(condition.speed_of_sound_mps ?? 340));
  const [liftCoefficient, setLiftCoefficient] = useState("");
  const [dragCoefficient, setDragCoefficient] = useState("");
  const [referenceAreaM2, setReferenceAreaM2] = useState(
    predictionReferenceAreaM2 != null && Number.isFinite(predictionReferenceAreaM2)
      ? String(predictionReferenceAreaM2)
      : "",
  );
  const [coefficientBasisConfirmed, setCoefficientBasisConfirmed] = useState(false);
  const [spanwiseText, setSpanwiseText] = useState("");
  const [spanwiseBasisConfirmed, setSpanwiseBasisConfirmed] = useState(false);
  const [state, setState] = useState<CompareState>("idle");
  const [message, setMessage] = useState("Import independently produced 3D evidence, then compare it with the canonical relaxed-wake artifact.");
  const [comparison, setComparison] = useState<AeroThreeDComparisonContent | null>(null);
  const [comparisonPredictionKey, setComparisonPredictionKey] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const ready = Boolean(projectId && geometryArtifactVersionId && predictionArtifactVersionId && predictionCurrent);
  const defaultSurfaceId = predictionSurfaceIds[0] ?? "unidentified-surface";
  const comparisonInputKey = JSON.stringify({
    prediction: { geometryArtifactVersionId, predictionArtifactVersionId, condition },
    reference: {
      sourceKind,
      sourceId: sourceId.trim(),
      runId: runId.trim(),
      fidelity: fidelity.trim(),
      referenceSpeedMps: referenceSpeedMps.trim(),
      referenceAlphaDeg: referenceAlphaDeg.trim(),
      referenceBetaDeg: referenceBetaDeg.trim(),
      referenceDensity: referenceDensity.trim(),
      referenceViscosity: referenceViscosity.trim(),
      referenceSoundSpeed: referenceSoundSpeed.trim(),
      liftCoefficient: liftCoefficient.trim(),
      dragCoefficient: dragCoefficient.trim(),
      referenceAreaM2: referenceAreaM2.trim(),
      coefficientBasisConfirmed,
      spanwiseText: spanwiseText.trim(),
      spanwiseBasisConfirmed,
      spanwiseBasis: matchingSpanwiseBasis,
    },
  });
  const stale = comparison != null && (
    comparisonPredictionKey !== comparisonInputKey || !predictionCurrent
  );

  useEffect(() => () => abortRef.current?.abort(), []);

  const compare = async () => {
    if (!ready || !projectId || !geometryArtifactVersionId || !predictionArtifactVersionId) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (!sourceId.trim()) throw new Error("Give the real 3D source a case, run, report, or dataset identifier");
      const integratedCoefficients = {
        lift_coefficient: optionalNumber(liftCoefficient),
        drag_coefficient: optionalNumber(dragCoefficient),
      };
      const hasIntegrated = Object.values(integratedCoefficients).some((value) => value != null);
      const referenceArea = optionalNumber(referenceAreaM2);
      if (hasIntegrated && (referenceArea == null || referenceArea <= 0)) {
        throw new Error("Integrated coefficients need their positive reference area S_ref");
      }
      if (hasIntegrated && !coefficientBasisConfirmed) {
        throw new Error("Confirm the imported coefficient normalization and wind-axis conventions");
      }
      const integrated = hasIntegrated ? {
        coefficient_basis: {
          reference_area_m2: referenceArea!,
          normalization: "q_inf_times_reference_area" as const,
          lift_axis: "projected_body_plus_z_normal_to_freestream" as const,
          drag_axis: "positive_freestream" as const,
        },
        ...integratedCoefficients,
      } : undefined;
      const spanwise = parseSpanwise(spanwiseText, defaultSurfaceId);
      if (spanwise && !spanwiseBasisConfirmed) {
        throw new Error("Confirm the imported spanwise coordinate, load-density, and wind-axis conventions");
      }
      if (!hasIntegrated && !spanwise) throw new Error("Enter at least one integrated coefficient or two spanwise load points");
      setState("saving");
      setMessage("Saving the imported 3D evidence as a first-class Thread artifact…");
      const referenceId = newUuid();
      const reference = await aeroWorkbenchApi.importThreadThreeDReference({
        project_id: projectId,
        idempotency_key: `aero:three-d-reference:${referenceId}`,
        target: {
          logical_id: referenceId,
          name: `3D reference · ${sourceId.trim()}`,
          metadata: { source_kind: sourceKind, imported_from_workbench: true },
        },
        geometry_artifact_version_id: geometryArtifactVersionId,
        source: {
          kind: sourceKind,
          source_id: sourceId.trim(),
          ...(runId.trim() ? { run_id: runId.trim() } : {}),
          fidelity: fidelity.trim() || "independent 3D reference",
        },
        condition: {
          speed_mps: optionalNumber(referenceSpeedMps) ?? condition.speed_mps,
          alpha_deg: optionalNumber(referenceAlphaDeg) ?? condition.alpha_deg,
          beta_deg: optionalNumber(referenceBetaDeg),
          density_kg_m3: optionalNumber(referenceDensity),
          dynamic_viscosity_pa_s: optionalNumber(referenceViscosity),
          speed_of_sound_mps: optionalNumber(referenceSoundSpeed),
        },
        ...(integrated ? { integrated } : {}),
        ...(spanwise ? { spanwise, spanwise_basis: matchingSpanwiseBasis } : {}),
      }, controller.signal);
      if (controller.signal.aborted) return;
      setState("comparing");
      setMessage("Aligning the stacked section/wake prediction with the imported coordinates…");
      const comparisonId = newUuid();
      const compared = await aeroWorkbenchApi.compareThreadThreeD({
        project_id: projectId,
        idempotency_key: `aero:three-d-comparison:${comparisonId}`,
        target: {
          logical_id: comparisonId,
          name: `3D comparison · ${sourceId.trim()}`,
          metadata: { reference_artifact_version_id: reference.artifact_version_id },
        },
        prediction_artifact_version_id: predictionArtifactVersionId,
        reference_artifact_version_id: reference.artifact_version_id,
      }, controller.signal);
      if (controller.signal.aborted) return;
      setComparison(compared.content);
      setComparisonPredictionKey(comparisonInputKey);
      const content = compared.content;
      setState(content.comparable ? "done" : "warning");
      setMessage(content.comparable
        ? "Comparison saved. Solid cyan is the reduced-order stack; dashed amber is imported 3D evidence."
        : `Comparison saved as descriptive only${content.warnings.length ? `: ${content.warnings.join(" · ")}` : "."}`);
    } catch (error) {
      if (controller.signal.aborted) return;
      setState("error");
      setMessage(error instanceof Error ? error.message : "Could not compare the 3D evidence");
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const cl = comparison?.integrated.metrics.lift_coefficient;
  const cd = comparison?.integrated.metrics.drag_coefficient;
  const ld = comparison?.integrated.metrics.lift_to_drag_ratio;
  const liftMetrics = comparison?.spanwise.metrics.lift_n_per_m;
  const coefficientBasis = comparison?.integrated.coefficient_basis;
  const spanwiseBasis = comparison?.spanwise.spanwise_basis;

  return (
    <section className="section-analysis-panel three-d-comparison-panel" data-testid="three-d-comparison-panel">
      <header className="three-d-comparison-header">
        <span>
          <strong>Reduced-order stack vs real 3D</strong>
          <small>2D section evidence → aircraft NN → relaxed free wake, compared only to supplied independent evidence</small>
        </span>
        <button type="button" disabled={!ready || state === "saving" || state === "comparing"} onClick={compare}>
          {state === "saving" ? "Saving evidence…" : state === "comparing" ? "Comparing…" : "Import & compare"}
        </button>
      </header>
      <div className="three-d-comparison-body">
        <aside className="three-d-reference-form">
          <label><span>Reference kind</span><select value={sourceKind} onChange={(event) => setSourceKind(event.target.value as AeroThreeDReferenceKind)}>
            <option value="flow360_rans">Flow360 RANS</option>
            <option value="rans">Other RANS / DES</option>
            <option value="panel">Independent panel solve</option>
            <option value="experiment">Experiment</option>
          </select></label>
          <label><span>Source ID</span><input value={sourceId} onChange={(event) => setSourceId(event.target.value)} placeholder="case / DOI / report ID" /></label>
          <label><span>Run ID</span><input value={runId} onChange={(event) => setRunId(event.target.value)} placeholder="optional" /></label>
          <label><span>Fidelity</span><input value={fidelity} onChange={(event) => setFidelity(event.target.value)} /></label>
          <fieldset>
            <legend>Reference condition</legend>
            <label><span>Speed m/s</span><input type="number" step="any" value={referenceSpeedMps} onChange={(event) => setReferenceSpeedMps(event.target.value)} /></label>
            <label><span>Alpha °</span><input type="number" step="any" value={referenceAlphaDeg} onChange={(event) => setReferenceAlphaDeg(event.target.value)} /></label>
            <details>
              <summary>Full condition</summary>
              <label><span>Beta °</span><input type="number" step="any" value={referenceBetaDeg} onChange={(event) => setReferenceBetaDeg(event.target.value)} /></label>
              <label><span>Density</span><input type="number" step="any" value={referenceDensity} onChange={(event) => setReferenceDensity(event.target.value)} /></label>
              <label><span>Viscosity</span><input type="number" step="any" value={referenceViscosity} onChange={(event) => setReferenceViscosity(event.target.value)} /></label>
              <label><span>Sound m/s</span><input type="number" step="any" value={referenceSoundSpeed} onChange={(event) => setReferenceSoundSpeed(event.target.value)} /></label>
            </details>
          </fieldset>
          <fieldset>
            <legend>Integrated coefficients</legend>
            <label><span>C<sub>L</sub></span><input type="number" step="any" value={liftCoefficient} onChange={(event) => setLiftCoefficient(event.target.value)} /></label>
            <label><span>C<sub>D</sub></span><input type="number" step="any" value={dragCoefficient} onChange={(event) => setDragCoefficient(event.target.value)} /></label>
            <label><span>Reference S<sub>ref</sub> m²</span><input aria-label="Reference S ref m²" type="number" min="0" step="any" value={referenceAreaM2} onChange={(event) => {
              setReferenceAreaM2(event.target.value);
              setCoefficientBasisConfirmed(false);
            }} /></label>
            <small>
              {predictionReferenceAreaM2 != null
                ? `Prefilled with the reduced-order prediction's ${predictionReferenceAreaM2.toLocaleString()} m². Verify the imported solver used the same area.`
                : "Enter the area used by the imported solver; Thread will not infer it."}
            </small>
            <label className="three-d-coefficient-confirm">
              <input
                type="checkbox"
                checked={coefficientBasisConfirmed}
                onChange={(event) => setCoefficientBasisConfirmed(event.target.checked)}
              />
              <span>I confirm C<sub>L</sub>/C<sub>D</sub> use q∞S<sub>ref</sub>, lift normal to freestream toward projected body +z, and drag along positive freestream.</span>
            </label>
          </fieldset>
          <label className="three-d-spanwise-input"><span>Spanwise points · prediction surfaces: {predictionSurfaceIds.join(", ") || "none"}</span><textarea value={spanwiseText} onChange={(event) => setSpanwiseText(event.target.value)} placeholder={`surface, body_y_m, lift_N_per_body_y_m, drag_N_per_body_y_m\n${defaultSurfaceId}, -3, 800, 22\n${defaultSurfaceId}, 0, 1200, 28\n${defaultSurfaceId}, 3, 800, 22`} /></label>
          <label className="three-d-coefficient-confirm">
            <input
              type="checkbox"
              checked={spanwiseBasisConfirmed}
              onChange={(event) => setSpanwiseBasisConfirmed(event.target.checked)}
            />
            <span>I confirm y is body-y; loads are N per Δbody-y; lift is normal to freestream toward projected body +z; drag is along positive freestream.</span>
          </label>
          <small>Values are importer-supplied. Thread records their source; it does not claim to have run the 3D solver.</small>
        </aside>
        <main className="three-d-comparison-result">
          {comparison ? (
            <>
              <header>
                <span>
                  <strong>{stale ? "Stale comparison" : comparison.comparable ? "Comparable cases" : "Descriptive only"}</strong>
                  <small>{comparison.reference.source.source_id} · {comparison.reference.source.fidelity}</small>
                  {coefficientBasis ? <small className={coefficientBasis.matches ? "is-match" : "is-mismatch"}>
                    Integrated coefficient basis {coefficientBasis.matches ? "matched" : "mismatch"} · S<sub>ref</sub> {formatResidual(coefficientBasis.checks.reference_area.reference_m2)} m² reference / {formatResidual(coefficientBasis.checks.reference_area.prediction_m2)} m² prediction
                  </small> : null}
                  {spanwiseBasis ? <small className={spanwiseBasis.matches ? "is-match" : "is-mismatch"}>
                    Spanwise basis {spanwiseBasis.matches ? "matched" : "mismatch"} · {spanwiseBasis.reference
                      ? `${spanwiseBasis.reference.coordinate} / ${spanwiseBasis.reference.load_density}`
                      : "undeclared reference basis"}
                  </small> : null}
                </span>
                <dl>
                  {cl ? <div><dt>ΔC<sub>L</sub></dt><dd>{formatResidual(cl.residual)}</dd></div> : null}
                  {cd ? <div><dt>ΔC<sub>D</sub></dt><dd>{formatResidual(cd.residual)}</dd></div> : null}
                  {ld ? <div><dt>ΔL/D</dt><dd>{formatResidual(ld.residual)}</dd></div> : null}
                  {liftMetrics ? <div><dt>L′ NRMSE</dt><dd>{liftMetrics.normalized_rmse == null ? "—" : `${(100 * liftMetrics.normalized_rmse).toFixed(1)}%`}</dd></div> : null}
                </dl>
              </header>
              {comparison.spanwise.available ? (
                <ThreeDSpanwiseOverlay comparison={comparison} />
              ) : (
                <div className="section-analysis-empty">
                  <strong>{comparison.integrated.available ? "Integrated comparison only" : "No overlapping comparison metric"}</strong>
                  <span>{comparison.spanwise.unavailable_reason ?? "The imported reference has no real spanwise loads; none were synthesized."}</span>
                </div>
              )}
              {comparison.warnings.length ? <ul className="three-d-comparison-warnings">{comparison.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : null}
            </>
          ) : (
            <div className="section-analysis-empty">
              <strong>{ready ? "Bring an independent 3D result" : "Persist a current Quick 3D run first"}</strong>
              <span>{ready
                ? "Paste integrated coefficients and/or real spanwise loads. Thread will save the reference and a provenance-linked comparison artifact."
                : "This comparison needs a canonical Thread geometry and relaxed-wake artifact. Local previews are not treated as validation evidence."}</span>
            </div>
          )}
        </main>
      </div>
      <footer className={`section-diagnostic-status is-${state}`} role={state === "error" ? "alert" : "status"}>
        <span>{stale ? "Prediction or imported reference inputs changed; rerun the comparison before using it." : message}</span>
        <em>{stale ? "stale comparison" : comparison ? `${Math.round(100 * comparison.spanwise.coverage.fraction)}% spanwise coverage` : "no synthetic reference data"}</em>
      </footer>
    </section>
  );
}
